import { useState } from "react";
import Button from "../../../assets/TakyonDesignSystem/components/Button/Button";
import IconNew from "../../../assets/TakyonDesignSystem/components/Icon/Icon";
import { backendAxios } from "../../../assets/Services/axios";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { SMALL_SCREEN } from "../../../assets/Utils/generic";

interface FeedbackPayload {
  value: number;
  message: string;
  screenWidth: string;
  screenHeight: string;
  userAgent: string;
  os: string;
  url: string;
}

class FeedbackController {
  static async submitFeedback(payload: FeedbackPayload) {
    const response = await backendAxios.post("/feedback/create", payload);
    return response.data;
  }
}

export default function DsFeedback() {
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });
  const [isMinimized, setIsMinimized] = useState(true);
  const [isStep1, setIsStep1] = useState(true);
  const [isLastStep, setIsLastStep] = useState(false);
  const [vote, setVote] = useState(-1);
  const [feedback, setFeedback] = useState("");
  const maxCharacters = 500;
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(
    sessionStorage.getItem("ds_feedback") || false,
  );

  const submitFeedback = async () => {
    try {
      const payload = {
        value: vote,
        message: feedback,
        screenWidth: window.innerWidth.toString(),
        screenHeight: window.innerHeight.toString(),
        userAgent: navigator.userAgent,
        os: navigator.platform,
        url: window.location.href,
      };
      await FeedbackController.submitFeedback(payload);
    } catch (error) {
      console.log(error);
    }
    setIsLastStep(true);

    setTimeout(() => {
      setSubmitted(true);
    }, 2000);

    sessionStorage.setItem("ds_feedback", "true");
  };

  if (submitted) {
    return null;
  }

  return (
    <motion.div
      animate={{
        width: isMinimized ? "40px" : "280px",
        height: isMinimized
          ? "40px"
          : isStep1
          ? "231px"
          : isLastStep
          ? "60px"
          : "290px",
        borderRadius: isMinimized ? "100%" : "8px",
        padding: isMinimized ? "8px" : "16px",
      }}
      transition={{
        duration: 0.3,
      }}
      className="text-white overflow-hidden"
      style={{
        position: isSmallScreen ? "fixed" : "absolute",
        bottom: isSmallScreen ? "15px" : "80px",
        right: "10px",
        zIndex: 10,
        backgroundColor: "#1f282d",
      }}
    >
      <motion.div
        animate={{
          opacity: isMinimized ? 0 : 1,
        }}
        transition={{
          duration: 0.2,
        }}
        style={{
          width: "250px",
          height: isStep1 ? "221px" : isLastStep ? "50px" : "280px",
          position: "absolute",
          bottom: "0",
          right: "16px",
          display: isMinimized ? "none" : "block",
        }}
      >
        {isStep1 ? (
          <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-between gap-2">
              <p className="fs-body-lg">{t("feedback.title")} 👋</p>
              <div>
                <IconNew
                  className="cursor-pointer"
                  icon="close"
                  fill={false}
                  hasFill
                  color="white"
                  onClick={() => {
                    setIsMinimized(true);
                  }}
                />
              </div>
            </div>
            <p className="fs-body-sm mt-2">{t("feedback.description")}</p>
            <div className="d-flex justify-content-between">
              {Array.from({ length: 7 }, (_, i) => i + 1).map((num) => (
                <div
                  key={num}
                  className="rounded-2 cursor-pointer d-flex align-items-center justify-content-center text-black"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#D9D9D9",
                  }}
                  onClick={() => {
                    setVote(num);
                    setIsStep1(false);
                  }}
                >
                  {num}
                </div>
              ))}
            </div>
            <div className="d-flex gap-2 justify-content-between">
              <p className="fs-body-sm">{t("feedback.disagree")}</p>
              <p className="fs-body-sm">{t("feedback.agree")}</p>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {!isLastStep ? (
              <>
                <div className="d-flex justify-content-between gap-2">
                  <p className="fs-body-lg">
                    {t("feedback.writeyourFeedback")}
                  </p>
                  <div>
                    <IconNew
                      className="cursor-pointer"
                      icon="close"
                      fill={false}
                      hasFill
                      color="white"
                      onClick={() => {
                        setIsMinimized(true);
                      }}
                    />
                  </div>
                </div>
                <p className="fs-body-sm mt-2"></p>
                <div className="position-relative mb-3">
                  <textarea
                    className="form-control"
                    style={{
                      resize: "none",
                      minHeight: "100px",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                    }}
                    value={feedback}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= maxCharacters) {
                        setFeedback(value);
                      }
                    }}
                    placeholder={t("feedback.placeholder")}
                    maxLength={maxCharacters}
                  />
                  <div className="d-flex justify-content-end mt-1">
                    <span className="fs-body-sm text-white-50">
                      {feedback.length}/{maxCharacters}
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <Button
                    size="sm"
                    text={t("feedback.send")}
                    onClick={submitFeedback}
                  />
                </div>
              </>
            ) : (
              <p className="fs-h3">{t("feedback.thanks")} 🎉</p>
            )}
          </div>
        )}
      </motion.div>
      <motion.div
        animate={{
          opacity: isMinimized ? 1 : 0,
          display: isMinimized ? "block" : "none",
        }}
        transition={{
          duration: 0.2,
        }}
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          display: isMinimized ? "block" : "none",
        }}
      >
        <IconNew
          className="cursor-pointer"
          icon="thumbs_up"
          fill={false}
          hasFill
          color="white"
          onClick={() => {
            setIsMinimized(false);
          }}
        />
      </motion.div>
    </motion.div>
  );
}
